import { useEffect, useMemo, useRef, useState } from "react";
import { ContextMenu, type ContextMenuItem } from "@/components/common/ContextMenu";
import { FilterButton } from "@/components/common/FilterButton";
import { Icon, type IconName } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { Spinner } from "@/components/common/Spinner";
import {
	impersonateUser,
	listUsers,
	reinviteUser,
	setUserActive,
	type CreateUserResult,
	type User,
	type UserStatus,
} from "@/lib/api";
import { dateFormatter } from "@/lib/format-datetime";
import {
	filterUsers,
	NO_USER_FILTER,
	USER_ROLE_LABELS,
	USER_ROLE_ORDER,
	USER_STATUS_LABELS,
	USER_STATUS_ORDER,
	userOrganizationOptions,
	type UserFilter,
} from "@/lib/user-filter";
import { CreateUserDialog } from "@/components/admin/CreateUserDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { InviteLinkDialog } from "@/components/admin/InviteLinkDialog";
import { EditUserAccessDialog } from "@/components/admin/EditUserAccessDialog";
import { resetUserMfa } from "@/lib/mfa-api";
import { USER_PERMISSIONS } from "@/lib/permissions";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

const COLUMNS: { key: keyof User; label: string }[] = [
	{ key: "id", label: "ID" },
	{ key: "lastName", label: "姓" },
	{ key: "firstName", label: "名" },
	{ key: "email", label: "メールアドレス" },
	{ key: "organizationName", label: "組織" },
	{ key: "role", label: "ロール" },
	{ key: "status", label: "ステータス" },
	{ key: "mfaEnabled", label: "多要素認証" },
	{ key: "createdAt", label: "作成日時" },
];

/** own: ログイン中のユーザーの組織だけ (組織画面)。all: 全組織 (管理ページ)。 */
export type UsersScope = "own" | "all";

export function UsersTab({ scope }: { scope: UsersScope }) {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [pendingDeactivate, setPendingDeactivate] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [pendingMfaReset, setPendingMfaReset] = useState<{ id: string; name: string } | null>(null);
	const [reissued, setReissued] = useState<CreateUserResult | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	// 開いているメニュー。ステータスと操作の 2 種類があるため `status:<id>` / `actions:<id>` で区別する。
	const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
	const [editingAccess, setEditingAccess] = useState<User | null>(null);
	// メニューから始めた操作の完了待ち。その間はバッジを押せなくし、同じ操作を重ねさせない。
	const [busyUserId, setBusyUserId] = useState<string | null>(null);
	const [filter, setFilter] = useState<UserFilter>(NO_USER_FILTER);
	const currentUserId = useCurrentUserStore((s) => s.id);
	const currentUserRole = useCurrentUserStore((s) => s.role);
	const currentOrganizationId = useCurrentUserStore((s) => s.organizationId);
	const canModerate = currentUserRole === "Admin" || currentUserRole === "SuperAdmin";
	const isSuperAdmin = currentUserRole === "SuperAdmin";
	// 超管理者には API が全組織のユーザーを返すため、組織画面では自組織に絞る。
	const scopedUsers = useMemo(
		() =>
			scope === "all" ? users : users.filter((u) => u.organizationId === currentOrganizationId),
		[scope, users, currentOrganizationId],
	);
	const columns = scope === "all" ? COLUMNS : COLUMNS.filter((c) => c.key !== "organizationName");
	const organizations = useMemo(() => userOrganizationOptions(scopedUsers), [scopedUsers]);
	const visibleUsers = useMemo(() => filterUsers(scopedUsers, filter), [scopedUsers, filter]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		listUsers()
			.then((data) => {
				if (!cancelled) setUsers(data);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [refreshKey]);

	const toggleMenu = (key: string) => setOpenMenuFor((cur) => (cur === key ? null : key));

	const handleDeactivateConfirm = async () => {
		if (!pendingDeactivate) return;
		try {
			await setUserActive(pendingDeactivate.id, false);
			setRefreshKey((k) => k + 1);
		} catch (err) {
			setActionError(err instanceof Error ? err.message : "ユーザーの無効化に失敗しました。");
		} finally {
			setPendingDeactivate(null);
		}
	};

	const handleMfaResetConfirm = async () => {
		if (!pendingMfaReset) return;
		await resetUserMfa(pendingMfaReset.id);
		setPendingMfaReset(null);
		setRefreshKey((k) => k + 1);
	};

	const handleReactivate = async (id: string) => {
		setActionError(null);
		setOpenMenuFor(null);
		setBusyUserId(id);
		try {
			await setUserActive(id, true);
			setRefreshKey((k) => k + 1);
		} catch (err) {
			setActionError(err instanceof Error ? err.message : "ユーザーの再有効化に失敗しました。");
		} finally {
			setBusyUserId(null);
		}
	};

	const handleReinvite = async (id: string) => {
		setActionError(null);
		setOpenMenuFor(null);
		setBusyUserId(id);
		try {
			const result = await reinviteUser(id);
			setReissued(result);
			setRefreshKey((k) => k + 1);
		} catch (err) {
			setActionError(err instanceof Error ? err.message : "招待リンクの発行に失敗しました。");
		} finally {
			setBusyUserId(null);
		}
	};

	const handleImpersonate = async (id: string) => {
		setActionError(null);
		setOpenMenuFor(null);
		setBusyUserId(id);
		try {
			await impersonateUser(id);
			// ストアやキャッシュが自分の状態を持っているため、全リロードで対象ユーザーとして開き直す。
			// 再読み込みが終わるまでバッジは押せないままにする。
			window.location.replace("/");
		} catch (err) {
			setBusyUserId(null);
			setActionError(
				err instanceof Error ? err.message : "ユーザーとしての操作開始に失敗しました。",
			);
		}
	};

	return (
		<div style={{ padding: 32, height: "100%", overflowY: "auto" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					height: 40,
					flexShrink: 0,
					marginBottom: 24,
				}}
			>
				<h2
					style={{
						fontSize: "var(--text-heading)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
						margin: 0,
						marginRight: 8,
					}}
				>
					ユーザー
				</h2>
				<FilterSelect
					icon="filter"
					allLabel="すべてのステータス"
					options={USER_STATUS_ORDER.map((s) => ({ id: s, label: USER_STATUS_LABELS[s] }))}
					value={filter.status}
					onChange={(status) => setFilter((f) => ({ ...f, status }))}
					testId="users-status-filter"
				/>
				<FilterSelect
					icon="user"
					allLabel="すべてのロール"
					options={USER_ROLE_ORDER.filter((r) => isSuperAdmin || r !== "SuperAdmin").map((r) => ({
						id: r,
						label: USER_ROLE_LABELS[r],
					}))}
					value={filter.role}
					onChange={(role) => setFilter((f) => ({ ...f, role }))}
					testId="users-role-filter"
				/>
				{scope === "all" && (
					<FilterSelect
						icon="grid"
						allLabel="すべての組織"
						options={organizations.map((o) => ({ id: o.id, label: o.name }))}
						value={filter.organizationId}
						onChange={(organizationId) => setFilter((f) => ({ ...f, organizationId }))}
						testId="users-organization-filter"
					/>
				)}
				<button
					type="button"
					style={{
						display: "flex",
						alignItems: "center",
						gap: 6,
						marginLeft: "auto",
						flexShrink: 0,
						padding: "7px 14px",
						background: "var(--accent-solid)",
						color: "var(--on-neutral-solid)",
						border: 0,
						borderRadius: "var(--radius-3)",
						fontSize: "var(--text-ui)",
						fontWeight: 500,
						fontFamily: "inherit",
						cursor: "pointer",
					}}
					onClick={() => setDialogOpen(true)}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLButtonElement).style.opacity = "0.88";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLButtonElement).style.opacity = "1";
					}}
				>
					<Icon name="plus" size={14} color="var(--on-neutral-solid)" />
					ユーザーを招待
				</button>
			</div>
			<CreateUserDialog
				scope={scope}
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onCreated={() => setRefreshKey((k) => k + 1)}
			/>
			<EditUserAccessDialog
				user={editingAccess}
				onClose={() => setEditingAccess(null)}
				onSaved={() => setRefreshKey((k) => k + 1)}
			/>
			<InviteLinkDialog
				open={reissued !== null}
				invite={reissued}
				onClose={() => setReissued(null)}
			/>
			<ConfirmDialog
				open={pendingDeactivate !== null}
				title="ユーザーを無効化"
				message={`「${pendingDeactivate?.name}」を無効化しますか？このユーザーはログインできなくなります。後から再有効化できます。`}
				confirmLabel="無効化"
				cancelLabel="キャンセル"
				onConfirm={handleDeactivateConfirm}
				onCancel={() => setPendingDeactivate(null)}
			/>
			<ConfirmDialog
				open={pendingMfaReset !== null}
				title="多要素認証をリセット"
				message={`「${pendingMfaReset?.name}」のパスキー・認証アプリ・リカバリーコードをすべて削除し、サインアウトさせます。端末を失くしたなど、本人から依頼があったときに使います。組織で多要素認証が必須なら、次のサインインで設定し直します。`}
				confirmLabel="リセット"
				onConfirm={handleMfaResetConfirm}
				onCancel={() => setPendingMfaReset(null)}
			/>
			{error && <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{error}</p>}
			{actionError && (
				<p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{actionError}</p>
			)}
			{!error && (
				<div
					style={{
						boxShadow: "var(--shadow-ring)",
						borderRadius: "var(--radius-4)",
						overflow: "hidden",
					}}
				>
					<table style={{ width: "100%", borderCollapse: "collapse" }}>
						<thead>
							<tr style={{ background: "var(--surface-2)" }}>
								{columns.map((col) => (
									<th
										key={col.key}
										className="text-caption text-fg-3"
										style={{
											padding: "10px 16px",
											textAlign: "left",
											color: "var(--fg-3)",
											fontWeight: 600,
											letterSpacing: "var(--tracking-label)",
											textTransform: "uppercase",
											borderBottom: "var(--pane-header-border)",
											whiteSpace: "nowrap",
											...(col.key === "id" && { width: 180 }),
										}}
									>
										{col.label}
									</th>
								))}
								<th style={{ width: 44, borderBottom: "var(--pane-header-border)" }} />
							</tr>
						</thead>
						<tbody className="select-text">
							{loading ? (
								<tr style={{ height: 44 }}>
									<td
										colSpan={columns.length + 1}
										style={{ textAlign: "center", verticalAlign: "middle" }}
									>
										<Spinner size={14} />
									</td>
								</tr>
							) : visibleUsers.length === 0 ? (
								<tr>
									<td
										colSpan={columns.length + 1}
										style={{
											padding: "24px 16px",
											color: "var(--fg-3)",
											fontSize: "var(--text-ui)",
											textAlign: "center",
										}}
									>
										{scopedUsers.length === 0
											? "ユーザーがいません"
											: "条件に一致するユーザーがいません"}
									</td>
								</tr>
							) : (
								visibleUsers.map((user, i) => (
									<tr
										key={user.id}
										style={{
											height: 44,
											borderBottom:
												i < visibleUsers.length - 1 ? "var(--pane-header-border)" : "none",
										}}
										onMouseEnter={(e) => {
											(e.currentTarget as HTMLTableRowElement).style.background =
												"var(--surface-2)";
										}}
										onMouseLeave={(e) => {
											(e.currentTarget as HTMLTableRowElement).style.background = "transparent";
										}}
									>
										<td
											className="font-mono text-title text-fg-2"
											style={{
												padding: "0 16px",
												color: "var(--fg-2)",
												fontSize: "var(--text-caption)",
											}}
										>
											{user.id}
										</td>
										<td
											style={{
												padding: "0 16px",
												color: "var(--fg-1)",
												fontSize: "var(--text-caption)",
											}}
										>
											{user.lastName}
										</td>
										<td
											style={{
												padding: "0 16px",
												color: "var(--fg-1)",
												fontSize: "var(--text-caption)",
											}}
										>
											{user.firstName}
										</td>
										<td
											style={{
												padding: "0 16px",
												color: "var(--fg-2)",
												fontSize: "var(--text-caption)",
											}}
										>
											{user.email}
										</td>
										{scope === "all" && (
											<td
												style={{
													padding: "0 16px",
													color: "var(--fg-2)",
													fontSize: "var(--text-caption)",
												}}
											>
												{user.organizationName}
											</td>
										)}
										<td style={{ padding: "0 16px" }}>
											<span
												className="select-none"
												title={user.role === "Member" ? permissionSummary(user) : undefined}
												style={{
													display: "inline-block",
													padding: "2px 8px",
													borderRadius: "var(--radius-full)",
													fontSize: "var(--text-caption)",
													fontWeight: 500,
													cursor: "default",
													// ロールは属性なので色を付けず、色は隣の状態 (アクティブなど) に譲る。
													background: "var(--surface-2)",
													color: "var(--fg-2)",
												}}
											>
												{USER_ROLE_LABELS[user.role]}
											</span>
										</td>
										<td style={{ padding: "0 16px" }}>
											<StatusCell
												status={user.status}
												canModerate={canModerate && user.id !== currentUserId}
												open={openMenuFor === `status:${user.id}`}
												busy={busyUserId === user.id}
												onToggle={() => toggleMenu(`status:${user.id}`)}
												onClose={() => setOpenMenuFor(null)}
												onReinvite={() => handleReinvite(user.id)}
												onReactivate={() => handleReactivate(user.id)}
											/>
										</td>
										<td
											data-testid={`user-mfa-${user.id}`}
											style={{
												padding: "0 16px",
												color: user.mfaEnabled ? "var(--success)" : "var(--fg-3)",
												fontSize: "var(--text-caption)",
												whiteSpace: "nowrap",
											}}
										>
											{user.mfaEnabled ? "設定済み" : "未設定"}
										</td>
										<td
											style={{
												padding: "0 16px",
												color: "var(--fg-2)",
												fontSize: "var(--text-caption)",
											}}
										>
											{dateFormatter.format(new Date(user.createdAt))}
										</td>
										<td style={{ padding: "0 8px", textAlign: "right" }}>
											<RowActionsMenu
												userId={user.id}
												canEditAccess={
													canModerate && user.id !== currentUserId && user.role !== "SuperAdmin"
												}
												// 成り代わりはログイン可能な (Active な) ユーザーに限る。バックエンドも同条件で拒否する。
												canImpersonate={
													isSuperAdmin &&
													user.id !== currentUserId &&
													user.role !== "SuperAdmin" &&
													user.status === "Active"
												}
												canDeactivate={
													canModerate &&
													user.id !== currentUserId &&
													(user.status === "Active" || user.status === "Invited")
												}
												canResetMfa={
													canModerate &&
													user.id !== currentUserId &&
													user.mfaEnabled &&
													(user.role !== "SuperAdmin" || isSuperAdmin)
												}
												open={openMenuFor === `actions:${user.id}`}
												busy={busyUserId === user.id}
												onToggle={() => toggleMenu(`actions:${user.id}`)}
												onClose={() => setOpenMenuFor(null)}
												onEditAccess={() => {
													setOpenMenuFor(null);
													setEditingAccess(user);
												}}
												onImpersonate={() => handleImpersonate(user.id)}
												onResetMfa={() => {
													setOpenMenuFor(null);
													setPendingMfaReset({
														id: user.id,
														name: `${user.lastName} ${user.firstName}`,
													});
												}}
												onDeactivate={() => {
													setOpenMenuFor(null);
													setPendingDeactivate({
														id: user.id,
														name: `${user.lastName} ${user.firstName}`,
													});
												}}
											/>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

const STATUS_THEMES: Record<UserStatus, { background: string; color: string }> = {
	Active: { background: "var(--success-subtle)", color: "var(--success)" },
	Invited: { background: "var(--warning-subtle)", color: "var(--warning)" },
	Inactive: { background: "var(--surface-2)", color: "var(--fg-2)" },
};

/**
 * 単一選択の絞り込みメニュー。null (先頭の「すべて〜」) は絞り込みなしを意味する。
 */
function FilterSelect<T extends string>({
	icon,
	allLabel,
	options,
	value,
	onChange,
	testId,
}: {
	icon: IconName;
	allLabel: string;
	options: { id: T; label: string }[];
	value: T | null;
	onChange: (value: T | null) => void;
	testId: string;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	return (
		<>
			<FilterButton
				open={menuOpen}
				ref={anchorRef}
				icon={icon}
				label={options.find((o) => o.id === value)?.label ?? allLabel}
				testId={testId}
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current, align: "left" }}
					onClose={() => setMenuOpen(false)}
					testId={`${testId}-menu`}
					minWidth={200}
					items={[
						{
							key: "all",
							label: allLabel,
							selectable: true,
							selected: value === null,
							testId: `${testId}-item-all`,
							onSelect: () => onChange(null),
						},
						{ separator: true },
						...options.map((o) => ({
							key: o.id,
							label: o.label,
							selectable: true,
							selected: o.id === value,
							testId: `${testId}-item-${o.id}`,
							onSelect: () => onChange(o.id),
						})),
					]}
				/>
			)}
		</>
	);
}

/** メンバーの権限の一覧 (ロールのバッジからは読み取れないため、ツールチップで示す)。 */
function permissionSummary(user: User): string {
	const labels = USER_PERMISSIONS.filter((p) => user.permissions.includes(p.value)).map(
		(p) => p.label,
	);
	return labels.length > 0 ? `権限: ${labels.join("、")}` : "権限なし";
}

interface RowActionsMenuProps {
	userId: string;
	canEditAccess: boolean;
	canImpersonate: boolean;
	canDeactivate: boolean;
	canResetMfa: boolean;
	open: boolean;
	busy: boolean;
	onToggle: () => void;
	onClose: () => void;
	onEditAccess: () => void;
	onImpersonate: () => void;
	onResetMfa: () => void;
	onDeactivate: () => void;
}

/** 行の右端の 3 点メニュー。権限の編集・成り代わり・多要素認証のリセット・無効化をまとめる。 */
function RowActionsMenu({
	userId,
	canEditAccess,
	canImpersonate,
	canDeactivate,
	canResetMfa,
	open,
	busy,
	onToggle,
	onClose,
	onEditAccess,
	onImpersonate,
	onResetMfa,
	onDeactivate,
}: RowActionsMenuProps) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const actions: ContextMenuItem[] = [];
	if (canEditAccess)
		actions.push({
			label: "ロールと権限を編集",
			testId: "user-action-edit-access",
			onSelect: onEditAccess,
		});
	if (canImpersonate)
		actions.push({
			label: "このユーザーとして操作",
			testId: "user-action-impersonate",
			onSelect: onImpersonate,
		});
	if (canResetMfa)
		actions.push({
			label: "多要素認証をリセット",
			destructive: true,
			testId: "user-action-reset-mfa",
			onSelect: onResetMfa,
		});
	if (canDeactivate)
		actions.push({
			label: "無効化",
			destructive: true,
			testId: "user-action-deactivate",
			onSelect: onDeactivate,
		});
	if (actions.length === 0) return null;

	return (
		<>
			<IconButton
				ref={buttonRef}
				icon="kebab"
				size={28}
				disabled={busy}
				data-testid={`user-actions-${userId}`}
				onClick={onToggle}
			/>
			{open && buttonRef.current && (
				<ContextMenu
					position={{ anchor: buttonRef.current, align: "right" }}
					onClose={onClose}
					items={actions}
					minWidth={180}
				/>
			)}
		</>
	);
}

interface StatusCellProps {
	status: UserStatus;
	canModerate: boolean;
	open: boolean;
	busy: boolean;
	onToggle: () => void;
	onClose: () => void;
	onReinvite: () => void;
	onReactivate: () => void;
}

function StatusCell({
	status,
	canModerate,
	open,
	busy,
	onToggle,
	onClose,
	onReinvite,
	onReactivate,
}: StatusCellProps) {
	const theme = STATUS_THEMES[status];
	const actions: ContextMenuItem[] = [];
	if (canModerate) {
		if (status === "Invited") actions.push({ label: "招待リンクを再発行", onSelect: onReinvite });
		if (status === "Inactive") actions.push({ label: "再有効化", onSelect: onReactivate });
	}

	if (actions.length === 0) {
		return (
			<span
				className="select-none"
				style={{
					display: "inline-block",
					padding: "2px 8px",
					borderRadius: "var(--radius-full)",
					fontSize: "var(--text-caption)",
					fontWeight: 500,
					cursor: "default",
					background: theme.background,
					color: theme.color,
				}}
			>
				{USER_STATUS_LABELS[status]}
			</span>
		);
	}
	return (
		<StatusMenuBadge
			open={open}
			busy={busy}
			onToggle={onToggle}
			onClose={onClose}
			label={USER_STATUS_LABELS[status]}
			background={theme.background}
			color={theme.color}
			actions={actions}
		/>
	);
}

interface StatusMenuBadgeProps {
	open: boolean;
	busy: boolean;
	onToggle: () => void;
	onClose: () => void;
	label: string;
	background: string;
	color: string;
	actions: ContextMenuItem[];
}

function StatusMenuBadge({
	open,
	busy,
	onToggle,
	onClose,
	label,
	background,
	color,
	actions,
}: StatusMenuBadgeProps) {
	const buttonRef = useRef<HTMLButtonElement>(null);

	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				onClick={onToggle}
				disabled={busy}
				className="select-none"
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: 4,
					padding: "2px 6px 2px 8px",
					border: 0,
					borderRadius: "var(--radius-full)",
					fontSize: "var(--text-caption)",
					fontWeight: 500,
					fontFamily: "inherit",
					cursor: busy ? "default" : "pointer",
					background,
					color,
				}}
			>
				{label}
				{busy ? (
					<Spinner size={8} color={color} />
				) : (
					<svg width="8" height="8" viewBox="0 0 8 8">
						<path
							d="M1 2.5L4 5.5L7 2.5"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				)}
			</button>
			{open && buttonRef.current && (
				<ContextMenu
					position={{ anchor: buttonRef.current, align: "left" }}
					onClose={onClose}
					items={actions}
					minWidth={180}
				/>
			)}
		</>
	);
}
